/**
 * GUID Normalization Utilities
 *
 * SINGLE SOURCE OF TRUTH for all GUID handling in the codebase.
 *
 * Problem: PostgreSQL/API returns lowercase GUIDs, seed data uses UPPERCASE,
 * and crypto.randomUUID() generates lowercase. Without normalization at
 * boundaries, === comparisons and Map/Set lookups silently fail.
 *
 * Solution: Normalize to UPPERCASE at every boundary (API response, seed data
 * ingestion, GUID generation). Internal code can then safely use === and
 * Map/Set lookups without per-site .toUpperCase() calls.
 *
 * Usage:
 *   import { normalizeGuid, guidEquals, guidKey } from '../utils/guid';
 *
 *   // Comparison:
 *   if (guidEquals(a, b)) { ... }
 *
 *   // Map/Set key:
 *   map.set(guidKey(projectGuid), value);
 *
 *   // Normalize at boundary:
 *   const normalized = normalizeGuid(apiResponse.projectId);
 */

/**
 * Normalize a GUID to uppercase, trimmed. Returns "" for null/undefined.
 * This is the canonical normalization — use at API boundaries so internal
 * code can safely use === for comparisons.
 */
export function normalizeGuid(guid?: string | null): string {
  return (guid ?? "").trim().toUpperCase();
}

/**
 * Case-insensitive GUID equality check.
 * Use this when you can't guarantee both sides are pre-normalized
 * (e.g. comparing a prop to a freshly-received API value).
 */
export function guidEquals(
  a?: string | null,
  b?: string | null
): boolean {
  return normalizeGuid(a) === normalizeGuid(b);
}

/**
 * Produce a normalized GUID suitable for use as a Map/Set/Record key.
 * Alias for normalizeGuid — exists for readability at call sites.
 */
export function guidKey(guid?: string | null): string {
  return normalizeGuid(guid);
}
